import { GiftCardField } from './GiftCardField.js';
import { useFormPageViewport } from '../../ui/use-form-page-viewport.js';
import {
  SESSION_STATUS_LABELS,
  type Client,
  type Session,
  type PricingPlanItem,
} from '@diana/contracts';
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowLeft } from 'lucide-react';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type ChangeEvent,
} from 'react';
import { Link } from 'react-router-dom';
import { Button, TextInput, TextArea, Select, useDianaUIPortal } from '../../ui/index.js';
import { CompactShootSchedule } from './CompactShootSchedule.js';
import { ClientQuickSearch } from './ClientQuickSearch.js';
import { formatScheduleLocal } from './schedule-time.js';
import { packageDurationMinutes } from './package-duration.js';
import { packagePrice } from './package-price.js';
import { formatCrmPriceLabel } from './crm-money.js';
import {
  CreateRequestCoordinator,
  emptyRequestDraft,
  type RequestCreationActions,
  type RequestDraft,
} from './create-request.js';
import './NewShootEditor.css';

export type NewShootEditorProps = RequestCreationActions & {
  clients: readonly Client[];
  packages?: readonly PricingPlanItem[];
  packagesError?: boolean;
  clientsLoading: boolean;
  clientsError: boolean;
  retryClients(): void;
  onCreated(session: Session): void;
  onCancel(): void;
};
const labels: Partial<Record<keyof RequestDraft, string>> = {
  name: 'Name',
  email: 'Email',
  phone: 'Phone',
  instagram: 'Instagram',
  title: 'Title',
  notes: 'Client message',
  internalNotes: 'Notes',
  dateTime: 'Date and time',
  location: 'Location',
  price: 'Price (EUR)',
};
const contactFields = [
  ['name', 'Name'],
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['instagram', 'Instagram'],
] as const;

export function NewShootEditor(props: NewShootEditorProps) {
  const [draft, setDraft] = useState(emptyRequestDraft);
  const [dirty, setDirty] = useState(false);
  const [discard, setDiscard] = useState(false);
  const [timeValid, setTimeValid] = useState(true);
  const actions = useRef(props);
  actions.current = props;
  const [coordinator] = useState(
    () =>
      new CreateRequestCoordinator({
        createClient: (body) => actions.current.createClient(body),
        createSession: (body) => actions.current.createSession(body),
      }),
  );
  const [snapshot, setSnapshot] = useState(coordinator.snapshot);
  const busy = useRef(false);
  const form = useRef<HTMLFormElement>(null);
  const { pageRef } = useFormPageViewport();
  const errorSummary = useRef<HTMLDivElement>(null);
  const lastNavigation = useRef<HTMLElement | null>(null);
  const portal = useDianaUIPortal();
  const clientDisabled = snapshot.pending || snapshot.clientLocked;
  const requestDisabled = snapshot.pending || snapshot.locked;
  const update = (patch: Partial<RequestDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
    setDirty(true);
  };
  useEffect(() => {
    if (!dirty || snapshot.completed) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, snapshot.completed]);
  useLayoutEffect(() => {
    if (snapshot.pending || (!snapshot.failure && Object.keys(snapshot.errors).length === 0))
      return;
    const first = form.current?.querySelector<HTMLElement>('[aria-invalid="true"]:not(:disabled)');
    (first ?? errorSummary.current)?.focus();
  }, [snapshot]);
  const leave = () => {
    if (snapshot.pending) return;
    if (!dirty && !snapshot.failure && !snapshot.createdClient) {
      props.onCancel();
      return;
    }
    lastNavigation.current = document.activeElement as HTMLElement;
    setDiscard(true);
  };
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy.current || snapshot.completed) return;
    busy.current = true;
    if (!timeValid) {
      busy.current = false;
      form.current
        ?.querySelector<HTMLElement>('.shoot-schedule-compact [aria-invalid="true"]')
        ?.focus();
      return;
    }
    const result = coordinator.run(draft);
    setSnapshot(coordinator.snapshot);
    try {
      const session = await result;
      setSnapshot(coordinator.snapshot);
      if (session) {
        setDirty(false);
        actions.current.onCreated(session);
      }
    } finally {
      busy.current = false;
    }
  }
  const fieldProps = (key: keyof RequestDraft) => ({
    id: `request-${key}`,
    'aria-label': labels[key],
    value: typeof draft[key] === 'string' ? draft[key] : '',
    'aria-invalid': Boolean(snapshot.errors[key]) || undefined,
    'aria-describedby': snapshot.errors[key] ? `request-${key}-error` : undefined,
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      update({ [key]: event.currentTarget.value }),
  });
  const fieldError = (key: keyof RequestDraft) =>
    snapshot.errors[key] ? (
      <span id={`request-${key}-error`} className="new-request__field-error">
        {snapshot.errors[key]}
      </span>
    ) : null;
  return (
    <article ref={pageRef} className="new-request" data-form-page>
      <Link
        className="dui-button dui-button--outline dui-button--medium"
        to="/crm/shoots"
        aria-disabled={snapshot.pending || undefined}
        onClick={(event) => {
          if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          )
            return;
          event.preventDefault();
          leave();
        }}
      >
        <ArrowLeft size={16} aria-hidden="true" />
        Back to Shoots
      </Link>
      <header className="new-request__heading">
        <h1>New shoot</h1>
        <p>Start with a client. Add the details you already know.</p>
      </header>
      <form ref={form} onSubmit={(event) => void submit(event)} noValidate aria-label="New shoot">
        <div className="new-request__columns">
          <section className="new-request__group" aria-labelledby="request-client-heading">
            <h2 id="request-client-heading">Client</h2>
            <fieldset className="new-request__choice" disabled={clientDisabled}>
              <legend className="sr-only">Client choice</legend>
              {(['existing', 'new'] as const).map((mode) => (
                <label key={mode}>
                  <input
                    type="radio"
                    name="client-mode"
                    checked={draft.mode === mode}
                    onChange={() => update({ mode })}
                  />
                  <span>{mode === 'existing' ? 'Existing client' : 'New client'}</span>
                </label>
              ))}
            </fieldset>
            {draft.mode === 'existing' ? (
              <ClientQuickSearch
                clients={props.clients}
                selected={draft.selectedClient}
                onSelect={(selectedClient) => update({ selectedClient })}
                disabled={clientDisabled}
                loading={props.clientsLoading}
                error={props.clientsError}
                retry={props.retryClients}
                validationError={snapshot.errors.selectedClient}
              />
            ) : (
              <>
                <p className="new-request__hint">A name is enough. Contact details are optional.</p>
                <fieldset className="new-request__client-fields" disabled={clientDisabled}>
                  <legend className="sr-only">New client details</legend>
                  {contactFields.map(([key, label]) => (
                    <label key={key} className="new-request__field">
                      <span>
                        {label}
                        {key === 'name' ? (
                          <span className="new-request__required">Required</span>
                        ) : null}
                      </span>
                      <TextInput
                        {...fieldProps(key)}
                        type={key === 'email' ? 'email' : key === 'phone' ? 'tel' : 'text'}
                        autoComplete={
                          key === 'name'
                            ? 'name'
                            : key === 'email'
                              ? 'email'
                              : key === 'phone'
                                ? 'tel'
                                : 'off'
                        }
                        aria-required={key === 'name' || undefined}
                      />
                      {fieldError(key)}
                    </label>
                  ))}
                </fieldset>
              </>
            )}
            {snapshot.createdClient && (
              <div className="new-request__saved">
                <strong>Client saved: {snapshot.createdClient.name}</strong>
                <p>
                  Client and request are saved separately. This client remains in Clients if you
                  leave before the request is created.
                </p>
                <Link
                  to={`/crm/clients/${snapshot.createdClient.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open saved client
                </Link>
              </div>
            )}
          </section>
          <section className="new-request__group" aria-labelledby="request-details-heading">
            <h2 id="request-details-heading">Shoot details</h2>
            <fieldset className="new-request__request-fields" disabled={requestDisabled}>
              <legend className="sr-only">Shoot details</legend>
              <label className="new-request__field">
                <span>Title</span>
                <TextInput
                  {...fieldProps('title')}
                  placeholder="e.g. Wedding in Porto"
                  maxLength={200}
                />
                {fieldError('title')}
                <span className="new-request__hint">Leave blank to use the client’s name.</span>
              </label>
              <label className="new-request__field">
                <span>Status</span>
                <Select
                  aria-label="Status"
                  value={draft.status}
                  onChange={(e) => update({ status: e.target.value as RequestDraft['status'] })}
                >
                  {(['NewRequest', 'InDiscussion', 'Scheduled'] as const).map((status) => (
                    <option key={status} value={status}>
                      {SESSION_STATUS_LABELS[status]}
                    </option>
                  ))}
                </Select>
              </label>
              <GiftCardField
                checked={draft.isGiftCard}
                disabled={requestDisabled}
                onCheckedChange={(isGiftCard) => update({ isGiftCard })}
              />
              <div className="new-request__package-row crm-form-pair">
                <label className="new-request__field">
                  <span>
                    Package <small>Optional</small>
                  </span>
                  <Select
                    aria-label="Package"
                    value={draft.package?.key ?? ''}
                    onChange={(e) => {
                      const plan = props.packages?.find((p) => p.key === e.target.value) ?? null;
                      const price = plan ? packagePrice(plan.payload.priceLabel) : null;
                      update({
                        package: plan,
                        ...(price
                          ? { price: String(price.amountMinor / 100), currency: price.currency }
                          : plan
                            ? { price: '' }
                            : {}),
                      });
                    }}
                  >
                    <option value="">No package</option>
                    {[...(props.packages ?? [])]
                      .sort((a, b) => a.position - b.position)
                      .map((plan) => (
                        <option key={plan.key} value={plan.key}>
                          {plan.payload.title} · {formatCrmPriceLabel(plan.payload.priceLabel)}
                        </option>
                      ))}
                  </Select>
                </label>
                <label className="new-request__field">
                  <span>Price ({draft.currency})</span>
                  <TextInput
                    {...fieldProps('price')}
                    aria-label={`Price (${draft.currency})`}
                    inputMode="decimal"
                    placeholder="Not set"
                  />
                  {fieldError('price')}
                </label>
              </div>
              {props.packagesError ? (
                <p className="new-request__hint">
                  Packages could not be loaded. You can still enter a price.
                </p>
              ) : null}
            </fieldset>
            <fieldset
              className="new-request__optional"
              disabled={requestDisabled}
              tabIndex={-1}
              aria-invalid={Boolean(snapshot.errors.dateTime) || undefined}
              aria-describedby={snapshot.errors.dateTime ? 'request-dateTime-error' : undefined}
            >
              <legend>
                When and where <small>Optional</small>
              </legend>
              <CompactShootSchedule
                scheduledStartAt={draft.dateTime ? new Date(draft.dateTime).toISOString() : null}
                scheduledEndAt={draft.scheduledEndAt}
                defaultDurationMinutes={packageDurationMinutes(draft.package?.payload)}
                onChange={(patch) =>
                  update({
                    ...(patch.scheduledStartAt !== undefined
                      ? { dateTime: formatScheduleLocal(patch.scheduledStartAt).slice(0, 16) }
                      : {}),
                    ...(patch.scheduledEndAt !== undefined
                      ? { scheduledEndAt: patch.scheduledEndAt }
                      : {}),
                  })
                }
                onValidityChange={setTimeValid}
              />
              {fieldError('dateTime')}
              <label className="new-request__field">
                <span>Location</span>
                <TextInput
                  {...fieldProps('location')}
                  placeholder="Venue or meeting point"
                  maxLength={500}
                />
                {fieldError('location')}
              </label>
            </fieldset>
            <fieldset className="new-request__optional" disabled={requestDisabled}>
              <legend>
                Message and notes <small>Optional</small>
              </legend>
              <label className="new-request__field">
                <span>
                  Client message <small>Optional</small>
                </span>
                <TextArea
                  {...fieldProps('notes')}
                  rows={3}
                  placeholder="Paste the original enquiry"
                />
                {fieldError('notes')}
                <span className="new-request__hint">
                  Kept as the original message after creation.
                </span>
              </label>
              <label className="new-request__field">
                <span>
                  Notes <small>Private · Optional</small>
                </span>
                <TextArea
                  {...fieldProps('internalNotes')}
                  rows={3}
                  placeholder="Plans, preferences or things to remember"
                />
                {fieldError('internalNotes')}
              </label>
            </fieldset>
          </section>
        </div>
        {snapshot.failure || Object.keys(snapshot.errors).length > 0 ? (
          <div className="new-request__error" role="alert" ref={errorSummary} tabIndex={-1}>
            <strong>
              {snapshot.failure ? 'Could not create the shoot' : 'Check the shoot details'}
            </strong>
            <p>
              {snapshot.failure?.message ??
                'Correct the highlighted fields, then create the shoot.'}
            </p>
            {snapshot.locked && (
              <p>Details are locked so a retry cannot create a different record.</p>
            )}
          </div>
        ) : null}
        <footer className="new-request__footer" data-form-footer>
          <Button variant="outline" onClick={leave} disabled={snapshot.pending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            loading={snapshot.pending}
            disabled={snapshot.completed}
          >
            {snapshot.pending
              ? 'Creating shoot…'
              : snapshot.locked
                ? 'Retry creation'
                : 'Create shoot'}
          </Button>
        </footer>
      </form>
      <Dialog.Root open={discard} onOpenChange={setDiscard}>
        <Dialog.Portal container={portal}>
          <Dialog.Overlay className="dui-dialog-overlay" />
          <Dialog.Content
            className="dui-dialog new-request__discard"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              lastNavigation.current?.focus();
            }}
          >
            <Dialog.Title>Discard this shoot?</Dialog.Title>
            <Dialog.Description>
              Your unsaved shoot details will be lost.
              {snapshot.createdClient ? ' The saved client will remain in Clients.' : ''}
              {snapshot.locked
                ? ' An earlier attempt may have saved a client or request. Check Clients and Shoots before starting again.'
                : ''}
            </Dialog.Description>
            <div className="new-request__discard-actions">
              <Button onClick={() => setDiscard(false)}>Keep editing</Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setDirty(false);
                  setDiscard(false);
                  props.onCancel();
                }}
              >
                Discard
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </article>
  );
}
